import tailwindClassnames from "../common/TailwindClassnames";
import { t } from "i18next";
import TextInput from "../common/TextInput";
import DateRangeSelector from "../common/DateRangeSelector";
import { MouseEventHandler, useState } from "react";
import { formatDateAsLocalString } from "../common/helpers";

type SearchBarDeploymentsProps = {
  canAddDeployment: boolean;
  onClickAddDeployment: MouseEventHandler;
  onChangeSearchQuery: Function;
  onSearch: Function;
  tempDeploymentsSearchQuery: any;
  /**
   * Opens the CSV import (AKL-815). Supplied by the parent only when the user may create
   * deployments — the same gate as "+ Add Deployment" — so the button renders on the prop
   * alone.
   */
  onImportFromCsv?: () => void;
};

const SearchBarDeployments = ({
  canAddDeployment,
  onClickAddDeployment,
  onChangeSearchQuery,
  onSearch,
  tempDeploymentsSearchQuery,
  onImportFromCsv,
}: SearchBarDeploymentsProps) => {
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();

  const onDateChange = (dates: any) => {
    let [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    // null (not "") when a bound is unset, so the search omits it entirely
    // instead of sending a literal `time_range_end=` — otherwise a start
    // date with no end date wouldn't return deployments that started after
    // it, since the empty end bound doesn't parse as "no upper bound".
    onChangeSearchQuery({
      target: {
        name: "time_range_start",
        value: start ? formatDateAsLocalString(start) : null,
      },
    });
    onChangeSearchQuery({
      target: {
        name: "time_range_end",
        value: end ? formatDateAsLocalString(end) : null,
      },
    });
  };

  return (
    <div className={tailwindClassnames.tableCardToolbar}>
      <TextInput
        name={"deployment_name"}
        label={t("Name")}
        placeholder={t("Search deployment name")}
        value={tempDeploymentsSearchQuery.deployment_name || ""}
        error={""}
        disabled={false}
        className={tailwindClassnames.tableFilterInput}
        type={"text"}
        initialFocus={true}
        onChange={(e: any) => onChangeSearchQuery(e)}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            onSearch();
          }
        }}
      />
      <TextInput
        name={"location_name"}
        label={t("Location")}
        placeholder={t("Search location name")}
        value={tempDeploymentsSearchQuery.location_name || ""}
        error={""}
        disabled={false}
        className={tailwindClassnames.tableFilterInput}
        type={"text"}
        initialFocus={false}
        onChange={(e: any) => onChangeSearchQuery(e)}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            onSearch();
          }
        }}
      />
      <TextInput
        name={"sensor_name"}
        label={t("Sensor")}
        placeholder={t("Search sensor name")}
        value={tempDeploymentsSearchQuery.sensor_name || ""}
        error={""}
        disabled={false}
        className={tailwindClassnames.tableFilterInput}
        type={"text"}
        initialFocus={false}
        onChange={(e: any) => onChangeSearchQuery(e)}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            onSearch();
          }
        }}
      />
      <div className="z-10">
        <DateRangeSelector
          label={`${t("Deployment Date Range")}`}
          startDate={startDate}
          endDate={endDate}
          onDateChange={(e: any) => onDateChange(e)}
          onKeyDown={(e: any) => {
            if (e.key === "Enter") {
              onSearch();
            }
          }}
        />
      </div>
      <button
        className={tailwindClassnames.btnFilterApply}
        onClick={() => onSearch()}
      >
        {t("Apply filters")}
      </button>
      <div className="flex-1" />
      {onImportFromCsv && (
        <button
          type="button"
          onClick={onImportFromCsv}
          className={tailwindClassnames.btnSecondary}
          data-testid="import-deployments-from-csv-toolbar"
        >
          {t("Import from CSV")}
        </button>
      )}
      {/* Conditional rendering, not a "hidden" class swap, so the gate
          can't be defeated by display utilities. */}
      {canAddDeployment && (
        <button
          onClick={onClickAddDeployment}
          className={tailwindClassnames.btnPrimary}
        >
          <span aria-hidden="true">+ </span>
          {t("Add Deployment")}
        </button>
      )}
    </div>
  );
};

export default SearchBarDeployments;
